/**
 * Sätze zu den Fehlercodes von Prüfen, Vorschau und Speichern der Einstellungen
 * (`ApartmentPricingConfigurationService`). Unbekannte Codes bleiben über `problemText` sichtbar.
 */
import { problemText, type ApiError } from '@/shared/api';

export const CONFIGURATION_PROBLEMS: Record<string, string> = {
  pricing_configuration_invalid: 'Die Prüfung auf dem Server verhindert das Speichern.',
  pricing_configuration_version_conflict:
    'Die Einstellungen wurden inzwischen an anderer Stelle geändert.',
  pricing_strategy_profile_version_conflict:
    'Das gewählte Strategieprofil ist nicht mehr aktuell – bitte neu wählen.',
  pricing_publication_controls_unavailable:
    'Die Publikationssteuerung ist nicht verfügbar – nur „Aus“ lässt sich speichern.',
  pricing_preview_attestation_required:
    'Für diesen Wechsel braucht es eine neue Prüfung der Wirkung.',
  pricing_preview_attestation_invalid: 'Die Prüfung der Wirkung ist ungültig oder schon verwendet.',
  pricing_preview_attestation_stale: 'Die Einstellungen haben sich seit der Prüfung geändert.',
  pricing_preview_attestation_expired: 'Die Prüfung der Wirkung ist abgelaufen.',
  pricing_ownership_adoption_invalid:
    'Bei diesem Wechsel gehen keine manuellen Werte an die Automatik über.',
  dynamic_pricing_confirmed_state_unavailable:
    'Der bestätigte Kalenderstand bei Channex ist nicht vollständig verfügbar.',
  dynamic_pricing_calendar_state_unavailable:
    'Der Kalenderstand des Apartments ist auf dem Server gerade nicht lesbar.',
  'Privilege.AccessDenied': 'Dafür fehlt dir das Recht „Verfügbarkeit & Vertrieb bearbeiten“.',
};

/** Nach einem Speichern ohne Antwort zeigt der neu geladene Stand: nichts wurde gespeichert. */
export const NOT_SAVED: ApiError = { status: 0, title: 'pricing_configuration_not_saved' };

/** Diese Fehler löst nur ein neu geladener Stand – „Neuen Stand laden“ verwirft dabei die Eingaben. */
const RELOAD = new Set([
  'pricing_configuration_version_conflict',
  'pricing_preview_attestation_stale',
]);
export const needsReload = (problem: ApiError | undefined) =>
  Boolean(problem && RELOAD.has(problem.title));

const text = (problem: ApiError | undefined, fallback: string) =>
  problem ? problemText(problem, fallback, { known: CONFIGURATION_PROBLEMS }) : null;

/** Satz zu einem Speicherfehler; ohne Antwort ist offen, ob gespeichert wurde. */
export function saveProblemText(problem: ApiError | undefined): string | null {
  if (problem === NOT_SAVED) {
    return 'Keine Antwort vom Server – laut neu geladenem Stand wurde nichts gespeichert.';
  }
  if (problem?.status === 0) {
    return 'Keine Antwort vom Server – ob gespeichert wurde, ist offen. Der Stand wird neu geladen.';
  }
  return text(problem, 'Die Einstellungen wurden nicht gespeichert.');
}

export const checkProblemText = (problem: ApiError | undefined) =>
  text(problem, 'Die Prüfung auf dem Server ist fehlgeschlagen – es wurde nichts gespeichert.');

export const previewProblemText = (problem: ApiError | undefined) =>
  text(problem, 'Die Wirkung konnte nicht berechnet werden – es wurde nichts gespeichert.');

/** Ein neuer Versuch brächte dasselbe Ergebnis – zurück zum Formular hilft. */
const FINAL = new Set([
  'pricing_strategy_profile_version_conflict',
  'pricing_publication_controls_unavailable',
  'Privilege.AccessDenied',
]);

export interface DialogProblem {
  text: string;
  /** Gescheitert ist die Aktivierung (nicht schon die Vorschau). */
  activation: boolean;
  retryable: boolean;
}

/** Grund im Dialog „Wirkung prüfen“: gescheitert ist die Aktivierung oder schon die Vorschau. */
export function dialogProblem(errors: {
  save?: ApiError;
  check?: ApiError;
  preview?: ApiError;
}): DialogProblem | null {
  const problem = errors.save ?? errors.check ?? errors.preview;
  const text =
    saveProblemText(errors.save) ??
    checkProblemText(errors.check) ??
    previewProblemText(errors.preview);
  if (!problem || !text) return null;
  return {
    text,
    activation: problem !== errors.preview,
    retryable: !FINAL.has(problem.title),
  };
}
